import React, { useEffect, useState } from 'react';
import { useSearchParams, Link /* useNavigate */ } from 'react-router-dom';
import { useAuth } from "../../hooks/useSupabaseAuth"

interface BookingDetails {
  id: string;
  status: string;
  amount_total: number;
  currency: string;
  customer_email: string;
  appointment_type: string;
  booking_date?: string;
  metadata?: {
    client_id?: string;
    appointment_type_id?: string;
  };
}

const BookingSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  // const navigate = useNavigate(); // TODO: Add navigation logic if needed
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookingDetails, setBookingDetails] = useState<BookingDetails | null>(null);
  const [autoLoggingIn, setAutoLoggingIn] = useState(false);
  const [loadingStep, setLoadingStep] = useState(1);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      handleAutoLogin(sessionId);
    } else {
      setError('No session ID provided');
      setLoading(false);
    }
  }, [sessionId]);

  const handleAutoLogin = async (sessionId: string) => {
    try {
      setAutoLoggingIn(true);
      setLoadingStep(1); // Verifying payment
      
      // Step 1: Get session token for immediate login
      const response = await fetch(`/api/auth/auto-login/booking?booking_id=${sessionId}`);
      
      if (response.ok) {
        // const data = await response.json(); // TODO: Use response data if needed for validation
        setLoadingStep(2); // Logging in
        
        // Session cookie should be set by the API response
        // No need to store in sessionStorage
        
        setLoadingStep(3); // Preparing appointment details
        
        // Verify the booking details
        const verifyResponse = await fetch(`/api/verify-session?session_id=${sessionId}`);
        if (verifyResponse.ok) {
          const bookingData = await verifyResponse.json();
          setBookingDetails(bookingData);
        }
        
        setLoading(false);
        setAutoLoggingIn(false);
        
      } else {
        // Fallback to regular verification
        await handleRegularVerification(sessionId);
      }
    } catch (error) {
      console.error('Auto-login failed:', error);
      await handleRegularVerification(sessionId);
    }
  };

  const handleRegularVerification = async (sessionId: string) => {
    try {
      const response = await fetch(`/api/verify-session?session_id=${sessionId}`);
      
      if (!response.ok) {
        throw new Error('Failed to verify session');
      }

      const data = await response.json();
      setBookingDetails(data);
    } catch (err) {
      console.error('Error verifying session:', err);
      setError('Failed to verify booking. Please contact support if payment was processed.');
    } finally {
      setLoading(false);
      setAutoLoggingIn(false);
    }
  };

  const getLoadingMessage = () => {
    if (!autoLoggingIn) return { title: 'Verifying your booking...', subtitle: 'Please wait while we confirm your payment.' };
    
    switch (loadingStep) {
      case 1:
        return { title: '🎉 Payment confirmed!', subtitle: 'Setting up your appointment...' };
      case 2:
        return { title: '🔐 Logging you in...', subtitle: 'Preparing your session...' };
      case 3:
        return { title: '📅 Preparing appointment details...', subtitle: 'Almost ready!' };
      default:
        return { title: 'Setting up your appointment...', subtitle: 'You\'ll be redirected shortly' };
    }
  };

  if (loading) {
    const { title, subtitle } = getLoadingMessage();
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
          <p className="text-gray-600 mb-4">{subtitle}</p>
          {autoLoggingIn && (
            <div className="flex justify-center space-x-2 mb-4">
              <div className={`w-3 h-3 rounded-full ${
                loadingStep >= 1 ? 'bg-green-500' : 'bg-gray-300'
              }`}></div>
              <div className={`w-3 h-3 rounded-full ${
                loadingStep >= 2 ? 'bg-green-500' : 'bg-gray-300'
              }`}></div>
              <div className={`w-3 h-3 rounded-full ${
                loadingStep >= 3 ? 'bg-green-500' : 'bg-gray-300'
              }`}></div>
            </div>
          )}
          <p className="text-xs text-gray-500">This usually takes just a few seconds</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Booking Verification Failed</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link 
            to="/client/portal" 
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Return to Client Portal
          </Link>
        </div>
      </div>
    );
  }

  if (!bookingDetails) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Booking Found</h2>
          <p className="text-gray-600 mb-6">Unable to find booking details for this session.</p>
          <Link 
            to="/client/portal" 
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Return to Client Portal
          </Link>
        </div>
      </div>
    );
  }

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-lg shadow-lg p-8">
        {/* Success Icon */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Booking Confirmed!</h1>
          <p className="text-gray-600">Your payment has been processed successfully.</p>
          {isAuthenticated && (
            <div className="mt-3 inline-flex items-center text-sm text-green-600">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              You're logged in and ready to access your client portal
            </div>
          )}
        </div>

        {/* Booking Details */}
        <div className="border-t border-gray-200 pt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Booking Details</h2>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Session Type:</span>
              <span className="font-medium text-gray-900">{bookingDetails.appointment_type}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Amount Paid:</span>
              <span className="font-medium text-gray-900">
                {formatAmount(bookingDetails.amount_total, bookingDetails.currency)}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Email:</span>
              <span className="font-medium text-gray-900">{bookingDetails.customer_email}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-600">Session ID:</span>
              <span className="font-mono text-sm text-gray-600">{bookingDetails.id}</span>
            </div>

            {bookingDetails.booking_date && (
              <div className="flex justify-between">
                <span className="text-gray-600">Appointment:</span>
                <span className="font-medium text-gray-900">{bookingDetails.booking_date}</span>
              </div>
            )}
          </div>
        </div>

        {/* Next Steps */}
        <div className="border-t border-gray-200 pt-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">What's Next?</h3>
          <ul className="text-gray-600 space-y-2 mb-6">
            <li className="flex items-start">
              <span className="text-green-600 mr-2">•</span>
              You'll receive a confirmation email shortly with your booking details
            </li>
            <li className="flex items-start">
              <span className="text-green-600 mr-2">•</span>
              Hailey will contact you to schedule your appointment time
            </li>
            <li className="flex items-start">
              <span className="text-green-600 mr-2">•</span>
              Check your client portal for updates and additional resources
            </li>
          </ul>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Link 
              to="/client/portal" 
              className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors text-center font-medium"
            >
              Go to Client Portal
            </Link>
            <Link 
              to="/" 
              className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-md hover:bg-gray-200 transition-colors text-center font-medium"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccess;